/**
 * @file ページの書き込みを解き直すための道具である (もう一度解く・回の切り替え・回を消す)。
 *
 * @what: 「もう一度解く」は確認のダイアログを一度はさみ、いまの線を n 回目として残してページを
 *   空にする。回の切り替えは「1回目 (9/12)」「2回目 (9/14)」「いま」の押しボタンで、残してある回が
 *   あるときだけ出す。過去の回を見ている間は「この回を消す」を出し、これも確認をはさむ。
 *   いずれも「ページに書き込む」の道具の並びの中に置く (それ以外のときは使わない)。
 * @why: 解き直しの区切りは本人が押して決める。過去の回は同じ紙面の上で見比べられれば足りるので、
 *   見る・消すだけにし、復元は持たない (代表の判断、2026-09-16)。
 * @failure: 描画のみ。
 */

import { useState } from "react";
import { PAGE_INK_ATTEMPTS_MAX } from "../lib/pageInk";
import { attemptDateLabel } from "../model/pageInkAttempt";
import { StudyDialog } from "./StudyDialog";

/** 「もう一度解く」のボタンと確認のダイアログである。 */
export function PageInkRestartButton({
  nextAttempt,
  disabled,
  limitReached,
  busy,
  onRestart,
}: {
  nextAttempt: number;
  disabled: boolean;
  limitReached: boolean;
  busy: boolean;
  onRestart: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <>
      <button
        type="button"
        className="study-hw-tool"
        disabled={disabled}
        title={limitReached ? `残せるのは${PAGE_INK_ATTEMPTS_MAX}回までです` : undefined}
        onClick={() => setConfirming(true)}
      >
        {busy ? "残しています..." : "もう一度解く"}
      </button>
      {confirming ? (
        <StudyDialog
          title="もう一度解きますか"
          labelledBy="study-page-ink-restart-title"
          onClose={() => setConfirming(false)}
        >
          <p>
            {`いまの書き込みを「${nextAttempt}回目」として残し、ページを空にします。残した回は道具の並びの「${nextAttempt}回目」から見られます。`}
          </p>
          <p className="study-ask-dialog-actions">
            <button type="button" className="study-ghost-btn" onClick={() => setConfirming(false)}>
              やめる
            </button>
            <button
              type="button"
              className="study-primary-btn"
              onClick={() => {
                setConfirming(false);
                onRestart();
              }}
            >
              残してやり直す
            </button>
          </p>
        </StudyDialog>
      ) : null}
    </>
  );
}

/** 回の切り替えと、過去の回を消すボタンである。残してある回が無ければ何も出さない。 */
export function PageInkAttemptChips({
  attempts,
  savedAt,
  viewing,
  busy,
  onView,
  onRemove,
}: {
  attempts: readonly number[];
  savedAt: ReadonlyMap<number, Date | null>;
  viewing: number | null;
  busy: boolean;
  onView: (attempt: number | null) => void;
  onRemove: (attempt: number) => void;
}) {
  const [removing, setRemoving] = useState<number | null>(null);
  if (attempts.length === 0) return null;
  return (
    <span className="study-hw-group" role="group" aria-label="解いた回">
      {attempts.map((n) => {
        const date = attemptDateLabel(savedAt.get(n));
        return (
          <button
            key={n}
            type="button"
            className="study-hw-tool"
            aria-pressed={viewing === n}
            disabled={busy}
            onClick={() => onView(n)}
          >
            {n}回目{date ? ` (${date})` : ""}
          </button>
        );
      })}
      <button
        type="button"
        className="study-hw-tool"
        aria-pressed={viewing === null}
        disabled={busy}
        onClick={() => onView(null)}
      >
        いま
      </button>
      {viewing !== null ? (
        <button
          type="button"
          className="study-hw-tool"
          disabled={busy}
          onClick={() => setRemoving(viewing)}
        >
          この回を消す
        </button>
      ) : null}
      {removing !== null ? (
        <StudyDialog
          title={`${removing}回目の書き込みを消しますか`}
          labelledBy="study-page-ink-remove-title"
          onClose={() => setRemoving(null)}
        >
          <p>消すと戻せません。</p>
          <p className="study-ask-dialog-actions">
            <button type="button" className="study-ghost-btn" onClick={() => setRemoving(null)}>
              やめる
            </button>
            <button
              type="button"
              className="study-danger-btn"
              onClick={() => {
                const n = removing;
                setRemoving(null);
                onRemove(n);
              }}
            >
              消す
            </button>
          </p>
        </StudyDialog>
      ) : null}
    </span>
  );
}
